import React from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowRight, FileIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuTrigger,
} from "@/components/ui/navigation-menu";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  getOrderedCategories,
  getCategoryConverterUrl,
} from "@/constants/categories";

const ConvertMegaMenu = () => {
  const allCategories = getOrderedCategories();
  const mainCategories = allCategories.slice(0, 8); // Main popular categories
  const moreCategories = allCategories.slice(8); // Remaining categories

  return (
    <NavigationMenuItem>
      <NavigationMenuTrigger
        className={cn(
          "text-sm font-medium transition-colors",
          "group inline-flex h-10 w-max items-center justify-center rounded-md px-4 py-2 text-sm font-medium transition-colors focus:outline-none bg-transparent",
          useLocation().pathname === "/convert"
            ? "bg-white text-black font-semibold"
            : "text-white",
          "hover:bg-white hover:text-black focus:bg-white",
        )}
      >
        Convert
      </NavigationMenuTrigger>
      <NavigationMenuContent>
        <div className="w-[800px] p-6 bg-white shadow-xl border-0 rounded-xl">
          {/* Main Categories Section */}
          <div className="mb-6">
            <h3 className="text-sm font-semibold text-gray-900 mb-4">
              Popular Converters
            </h3>
            <div className="grid grid-cols-4 gap-4">
              {mainCategories.map((category) => {
                const IconComponent = category.icon;
                const converterUrl = `/${category.slug}-converter`;

                return (
                  <Card
                    key={category.slug}
                    className="group border-0 shadow-none hover:shadow-md transition-all duration-200 hover:-translate-y-0.5"
                  >
                    <CardContent className="p-0">
                      <Link
                        to={converterUrl}
                        className="block p-4 text-center h-full hover:bg-gray-50 rounded-lg transition-colors"
                        title={`${category.name} Converter`}
                      >
                        <div className="flex flex-col items-center space-y-3">
                          <div
                            className={cn(
                              "w-12 h-12 rounded-lg flex items-center justify-center shadow-sm",
                              category.color,
                            )}
                          >
                            <IconComponent className="w-6 h-6 text-white" />
                          </div>
                          <div>
                            <div className="text-sm font-semibold text-gray-900 group-hover:text-primary transition-colors">
                              {category.name} Converter
                            </div>
                          </div>
                        </div>
                      </Link>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>

          {/* More Categories Section */}
          {moreCategories.length > 0 && (
            <div className="border-t border-gray-100 pt-4">
              <h3 className="text-sm font-semibold text-gray-900 mb-3">
                More Converters
              </h3>
              <div className="grid grid-cols-5 gap-3">
                {moreCategories.map((category) => {
                  const IconComponent = category.icon;
                  const converterUrl = `/${category.slug}-converter`;

                  return (
                    <Link
                      key={category.slug}
                      to={converterUrl}
                      className="group p-3 rounded-lg hover:bg-gray-50 transition-colors text-center"
                      title={`${category.name} Converter`}
                    >
                      <div className="flex flex-col items-center space-y-2">
                        <div
                          className={cn(
                            "w-8 h-8 rounded-md flex items-center justify-center",
                            category.color,
                          )}
                        >
                          <IconComponent className="w-4 h-4 text-white" />
                        </div>
                        <div className="text-xs font-medium text-gray-700 group-hover:text-primary transition-colors">
                          {category.name} Converter
                        </div>
                      </div>
                    </Link>
                  );
                })}
              </div>
            </div>
          )}

          {/* Footer - Professional links */}
          <div className="border-t border-gray-100 mt-6 pt-4">
            <div className="flex items-center justify-between text-sm">
              <Link
                to="/convert"
                className="flex items-center text-primary hover:text-primary/80 font-medium transition-colors"
              >
                <FileIcon className="w-4 h-4 mr-2" />
                Universal File Converter
              </Link>
              <Link
                to="/formats"
                className="text-gray-500 hover:text-primary transition-colors flex items-center"
              >
                View All 432+ Formats
                <ArrowRight className="w-3 h-3 ml-1" />
              </Link>
            </div>
          </div>
        </div>
      </NavigationMenuContent>
    </NavigationMenuItem>
  );
};

export default ConvertMegaMenu;
